import { useState, type ReactNode } from 'react'
import { Box, Collapse, IconButton, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import TAChip from './TAChip'
import { FS_SM, ICON_SM } from '../common/theme'

interface EvalGroupSectionProps {
  name: string
  children?: ReactNode
  // Right-side header content, e.g. pass/fail counts, score percent.
  // Sits between the chip + actions cluster and the expand toggle.
  headerExtras?: ReactNode
  // Action icons rendered just after the chip (e.g. Save / Edit
  // template buttons on the scored view).
  actions?: ReactNode
  // When set, the body is suppressed and this message renders next
  // to the chip instead — used by the improvements view to flag
  // "None" for groups with no improvements to apply.
  emptyMessage?: string
  defaultExpanded?: boolean
}

// Shared shell for a scoring-template group card — same chip
// header, sky-tinted strip and border that the scored evaluation
// and improvements view both render. The caller supplies the
// category-level children plus any header extras / actions.
export default function EvalGroupSection({
  name, children, headerExtras, actions, emptyMessage, defaultExpanded = true,
}: EvalGroupSectionProps) {
  const [showCategories, setShowCategories] = useState(defaultExpanded)
  const empty = !!emptyMessage
  return (
    <Box sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        px: 0.75, py: 0.5,
        bgcolor: `#00000020`,
        borderBottom: empty ? 0 : 1, borderColor: 'divider',
      }}>
        <TAChip kind="scoretemplate" label={name} />
        {actions}
        {empty && (
          <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.4 }}>
            {emptyMessage}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {headerExtras}
        {!empty && (
          <IconButton size="small" onClick={() => setShowCategories(v => !v)} sx={{ p: 0.2 }}>
            {showCategories ? <ExpandLessIcon sx={{ fontSize: ICON_SM }} /> : <ExpandMoreIcon sx={{ fontSize: ICON_SM }} />}
          </IconButton>
        )}
      </Box>
      {!empty && (
        <Collapse in={showCategories}>
          <Box>{children}</Box>
        </Collapse>
      )}
    </Box>
  )
}
